import type { ID } from '@octopus-do/octopus-project-core'

import type { AppState } from '@/types'

/**
 * Visibility of artboard nodes while the page-tag filter is active (OCT-5007).
 *
 * Filtering by page tags hides every branch that contains no matching page
 * instead of greying non-matching pages out: on a large sitemap the greyed-out
 * pages still had to be scrolled past to find the matching ones.
 *
 * Rules:
 * - a page owning one of the selected tags is `matched` — rendered at full opacity;
 * - an ancestor of a matched page stays rendered but dimmed, so the path to the
 *   page is readable;
 * - anything else is not rendered at all;
 * - a selected page is always rendered, even without a matching tag, so that
 *   creating, pasting or untagging a page under an active filter does not make
 *   it vanish under the cursor;
 * - a collapsed node or section holding a match is expanded for the duration of
 *   the filter only — the `collapsed` flag in the project is never written, so
 *   clearing the filter restores the layout and collaborators are unaffected.
 */
export interface TagVisibility {
  filterIsActive: boolean
  /** nodes owning one of the selected tags */
  matchedNodeIds: Set<ID>
  /** nodes to render: matched + pinned nodes and all of their ancestors */
  visibleNodeIds: Set<ID>
  /** collapsed nodes/sections holding something visible; expanded temporarily */
  forceExpandedIds: Set<ID>
}

const EMPTY_SET: Set<ID> = new Set()

export const INACTIVE_VISIBILITY: TagVisibility = {
  filterIsActive: false,
  matchedNodeIds: EMPTY_SET,
  visibleNodeIds: EMPTY_SET,
  forceExpandedIds: EMPTY_SET,
}

const computeVisibility = (
  selectedTags: ID[],
  tags: { id: ID; nodes: ID[] }[],
  dependencies: AppState['relations']['dependencies'],
  pinnedNodeIds: ID[]
): TagVisibility => {
  if (selectedTags.length === 0) {
    return INACTIVE_VISIBILITY
  }

  const selected = new Set(selectedTags)
  const matchedNodeIds = new Set<ID>()

  tags.forEach((tag) => {
    if (!selected.has(tag.id)) {
      return
    }

    tag.nodes.forEach((nodeId) => {
      // a tag can still reference a deleted node
      if (dependencies[nodeId]) {
        matchedNodeIds.add(nodeId)
      }
    })
  })

  const visibleNodeIds = new Set<ID>()
  const forceExpandedIds = new Set<ID>()

  // walk up from every node that has to be on screen, collecting its ancestors;
  // an ancestor is by definition holding something visible, so it also has to
  // render its children even when the project marks it collapsed
  const reveal = (nodeId: ID) => {
    if (visibleNodeIds.has(nodeId)) {
      return
    }

    visibleNodeIds.add(nodeId)

    let current = dependencies[nodeId]

    while (current) {
      const parentId: ID | undefined = current.parents[0]

      if (!parentId || !dependencies[parentId]) {
        return
      }

      forceExpandedIds.add(parentId)

      if (visibleNodeIds.has(parentId)) {
        return
      }

      visibleNodeIds.add(parentId)
      current = dependencies[parentId]
    }
  }

  matchedNodeIds.forEach(reveal)

  pinnedNodeIds.forEach((nodeId) => {
    if (dependencies[nodeId]) {
      reveal(nodeId)
    }
  })

  return {
    filterIsActive: true,
    matchedNodeIds,
    visibleNodeIds,
    forceExpandedIds,
  }
}

let cache: {
  selectedTags: ID[]
  tags: unknown
  dependencies: unknown
  pinnedNodeIds: ID[]
  result: TagVisibility
} | null = null

/**
 * Memoised by reference on the four inputs, so every node on the artboard can
 * read it during a render without recomputing the walk.
 */
export const selectTagVisibility = (state: AppState): TagVisibility => {
  const { selectedTags } = state.canvas.tags
  const { tags } = state.manager.project
  const { dependencies } = state.relations
  const pinnedNodeIds = state.canvas.node.selectedNodes

  if (
    cache &&
    cache.selectedTags === selectedTags &&
    cache.tags === tags &&
    cache.dependencies === dependencies &&
    cache.pinnedNodeIds === pinnedNodeIds
  ) {
    return cache.result
  }

  const result = computeVisibility(
    selectedTags,
    tags,
    dependencies,
    pinnedNodeIds
  )

  cache = { selectedTags, tags, dependencies, pinnedNodeIds, result }

  return result
}

/** for tests and for resetting between projects */
export const resetTagVisibilityCache = () => {
  cache = null
}

export const selectNodeIsVisible =
  (nodeId: ID) =>
  (state: AppState): boolean => {
    const { filterIsActive, visibleNodeIds } = selectTagVisibility(state)

    return !filterIsActive || visibleNodeIds.has(nodeId)
  }

/**
 * Keeps only the ids to render, in their original order. Callers must derive
 * first/last-child flags from the result, not from the full child list, or the
 * tree connector lines are drawn for children that are not there.
 */
export const selectVisibleIds =
  (ids: ID[]) =>
  (state: AppState): ID[] => {
    const { filterIsActive, visibleNodeIds } = selectTagVisibility(state)

    if (!filterIsActive) {
      return ids
    }

    return ids.filter((id) => visibleNodeIds.has(id))
  }

/**
 * `true` when the node must render its children despite being collapsed in the
 * project, because a matching page lives inside it.
 */
export const selectIsForceExpanded =
  (nodeId: ID) =>
  (state: AppState): boolean => {
    const { filterIsActive, forceExpandedIds } = selectTagVisibility(state)

    return filterIsActive && forceExpandedIds.has(nodeId)
  }

/**
 * `true` when the filter is on and not one of `ids` survives it — used to tell
 * an empty tab from a tab that simply has no sections.
 */
export const selectNothingVisibleAmong =
  (ids: ID[]) =>
  (state: AppState): boolean => {
    const { filterIsActive, visibleNodeIds } = selectTagVisibility(state)

    return filterIsActive && !ids.some((id) => visibleNodeIds.has(id))
  }

/** node is rendered only as a path to a match, so it is dimmed */
export const selectNodeIsDimmedByTagFilter =
  (nodeId: ID) =>
  (state: AppState): boolean => {
    const { filterIsActive, matchedNodeIds } = selectTagVisibility(state)

    return filterIsActive && !matchedNodeIds.has(nodeId)
  }
